import React, { useContext, useEffect, useRef, useState } from "react";

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { DatabaseContext } from "../../context";
import ProjectAudioFileComponent from "../projects/Components/File Types/ProjectAudioFileComponent";
import ProjectImageFileComponent from "../projects/Components/File Types/ProjectImageFileComponent";
import ProjectPDFFileComponent from "../projects/Components/File Types/ProjectPDFFileComponent";
import ProjectVideoFileComponent from "../projects/Components/File Types/ProjectVideoFileComponent";

export default function SurveyFileComponent({
    question,
    isEditing = false,
    moveUp,
    moveDown,
    delete: deleteQuestion,
}) {
    const { files = [], filesLoaded = false } = useContext(DatabaseContext);
    const containerRef = useRef(null);
    const [contentWidth, setContentWidth] = useState();

    const file = files.find((item) => item.key === question?.linkKey);
    const fileType = question?.fileType || "";

    useEffect(() => {
        const updateContentWidth = () => {
            const availableWidth = containerRef.current?.clientWidth;
            if (availableWidth) {
                setContentWidth(Math.min(Math.floor(availableWidth), 900));
            }
        };

        updateContentWidth();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateContentWidth);
            if (containerRef.current) {
                observer.observe(containerRef.current);
            }
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateContentWidth);
        return () =>
            window.removeEventListener("resize", updateContentWidth);
    }, []);

    function renderFile() {
        if (!filesLoaded) {
            return (
                <Box
                    role="status"
                    sx={{
                        minHeight: 140,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.25,
                        px: 2,
                        borderRadius: 2,
                        color: "text.secondary",
                        backgroundColor: "background.subtle",
                    }}
                >
                    <CircularProgress size={28} aria-label="Loading resource" />
                    <Typography variant="body2">
                        Loading resource…
                    </Typography>
                </Box>
            );
        }

        if (!file || !contentWidth) {
            return (
                <Box
                    sx={{
                        minHeight: 110,
                        display: "grid",
                        placeItems: "center",
                        px: 2,
                        borderRadius: 2,
                        color: "text.secondary",
                        backgroundColor: "background.subtle",
                    }}
                >
                    <Typography variant="body2">
                        {file
                            ? "Preparing resource preview…"
                            : "This linked file is unavailable."}
                    </Typography>
                </Box>
            );
        }

        if (fileType.includes("video")) {
            return (
                <ProjectVideoFileComponent
                    file={file}
                    contentWidth={contentWidth}
                />
            );
        }
        if (fileType.includes("image")) {
            return (
                <ProjectImageFileComponent
                    file={file}
                    contentWidth={contentWidth}
                />
            );
        }
        if (fileType.includes("audio")) {
            return (
                <ProjectAudioFileComponent
                    file={file}
                    contentWidth={contentWidth}
                />
            );
        }
        if (fileType === "application/pdf") {
            return (
                <ProjectPDFFileComponent
                    file={file}
                    contentWidth={contentWidth}
                />
            );
        }

        return (
            <Typography variant="body2" color="text.secondary">
                A preview is not available for this file type.
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: isEditing
                    ? "minmax(0, 1fr) auto"
                    : "minmax(0, 1fr)",
                alignItems: "start",
                gap: 1,
                width: "100%",
                minWidth: 0,
            }}
        >
            <Box ref={containerRef} sx={{ width: "100%", minWidth: 0 }}>
                {renderFile()}
            </Box>

            {isEditing && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Tooltip title="Move up">
                        <IconButton
                            size="small"
                            aria-label="Move resource up"
                            onClick={() => moveUp?.(question)}
                        >
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Move down">
                        <IconButton
                            size="small"
                            aria-label="Move resource down"
                            onClick={() => moveDown?.(question)}
                        >
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete resource"
                            onClick={() => deleteQuestion?.(question)}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
}
